import * as React from 'react';
import { IFilterControl, IFilterItem, ITypeOption } from '../../FilterSetupPlaceholder';
import { OperatorSelector, IOperator } from '../OperatorSelector';
import styles from './FolderTypeFilterControl.module.css';
import { AtollonCheckbox } from '../../../../../../ui/Checkbox/AtollonCheckbox';
import { Link, Text } from '@adobe/react-spectrum';
import { useFilterGuard } from '../../../../../../../hooks/useFilterGuard';

export interface IFolderTypeFilterControlProps {
  filter: IFilterControl;
  onFilterChange: (filter: IFilterControl) => void;
  options: ITypeOption[];
}

const TYPE_OPERATORS: IOperator[] = [
  { key: 'in', label: 'In' },
  { key: 'not in', label: 'Not In' },
];

export const FolderTypeFilterControl = React.forwardRef<HTMLDivElement, IFolderTypeFilterControlProps>(
  (props, forwardedRef) => {
    const { filter, onFilterChange, options } = props;
    const { notifyChange, syncGuard } = useFilterGuard(onFilterChange);
    const isInternalUpdateRef = React.useRef(false);
    const lastProcessedFilterInfoRef = React.useRef<string>('');
    const convertedInputsRef = React.useRef<Set<string>>(new Set());
    
    const currentFilterItem = React.useMemo(
      () =>
        filter.filterInfo[0] || {
          name: 'value',
          type: 'in',
          value: '',
        },
      [filter.filterInfo]
    );

    const [operator, setOperator] = React.useState<string>(currentFilterItem.type || 'in');
    const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(
      currentFilterItem.value ? new Set(currentFilterItem.value.split(',')) : new Set()
    );

    React.useEffect(() => {
      syncGuard(filter);
    }, [filter, syncGuard]);

    React.useEffect(() => {
      const currentItem = filter.filterInfo[0];
      
      // Create a signature for the current filterInfo to detect actual changes
      const filterInfoSignature = JSON.stringify({
        type: currentItem?.type,
        value: currentItem?.value,
        name: currentItem?.name,
      });
      
      // Skip if this is the same filterInfo we already processed (output)
      if (lastProcessedFilterInfoRef.current === filterInfoSignature) {
        return;
      }
      
      // Skip if this is an internal update (from updateFilterInfo)
      if (isInternalUpdateRef.current) {
        lastProcessedFilterInfoRef.current = filterInfoSignature;
        return;
      }
      
      // Handle empty/undefined filterInfo - explicitly clear state
      if (!currentItem) {
        const emptySignature = JSON.stringify({ type: null, value: null, name: null });
        // Always clear state when filterInfo is empty, even if we've seen this signature before
        // This ensures UI stays in sync with empty state
        if (lastProcessedFilterInfoRef.current !== emptySignature || selectedKeys.size > 0) {
          lastProcessedFilterInfoRef.current = emptySignature;
          setOperator('in');
          setSelectedKeys(new Set());
        }
        return;
      }
      
      // Initialize with default 'in' operator if filterInfo is empty or has old operator types
      // BUT: Track the INPUT signature to prevent re-converting the same input
      const needsConversion = !currentItem.type || currentItem.type === 'equals' || currentItem.type === 'not_equals' || currentItem.type === 'contains';
      
      if (needsConversion) {
        // Create input signature to track what we're about to convert
        const inputSignature = JSON.stringify({
          type: currentItem?.type || 'missing',
          value: currentItem?.value || '',
          name: currentItem?.name || 'value',
        });
        
        // If we've already converted this exact input before, skip it
        if (convertedInputsRef.current.has(inputSignature)) {
          // Still mark the output as processed to prevent further processing
          lastProcessedFilterInfoRef.current = filterInfoSignature;
          // But still clear state if value is empty
          if (!currentItem.value || currentItem.value.trim() === '') {
            setSelectedKeys(new Set());
            setOperator('in');
          }
          return;
        }
        
        // Mark this input as converted
        convertedInputsRef.current.add(inputSignature);
        
        const defaultFilterInfo: IFilterItem[] = [
          {
            name: 'value',
            type: 'in',
            value: currentItem.value || '', // Ensure empty string for empty values
            valueLabel: currentItem.valueLabel || '',
          },
        ];
        
        // Clear local state immediately if value is empty
        if (!currentItem.value || currentItem.value.trim() === '') {
          setSelectedKeys(new Set());
          setOperator('in');
        }
        
        // Mark the OUTPUT filterInfo as processed BEFORE calling onFilterChange
        const outputSignature = JSON.stringify({
          type: 'in',
          value: defaultFilterInfo[0].value,
          name: defaultFilterInfo[0].name,
        });
        lastProcessedFilterInfoRef.current = outputSignature;
        
        const didNotify = notifyChange({
          ...filter,
          filterInfo: defaultFilterInfo,
        });
        if (didNotify) {
          isInternalUpdateRef.current = true;
          setTimeout(() => { isInternalUpdateRef.current = false; }, 0);
        }
        
        return;
      }
      
      // Update state from filterInfo (only if type is 'in' or 'not in')
      if (currentItem.type === 'in' || currentItem.type === 'not in') {
        // Mark as processed
        lastProcessedFilterInfoRef.current = filterInfoSignature;
        
        setOperator(currentItem.type);
        // Clear selectedKeys if value is empty/null/undefined
        const valueStr = currentItem.value || '';
        const isEmptyValue = !valueStr || valueStr.trim() === '';
        if (isEmptyValue) {
          // Always create a new Set to ensure React detects the change
          // Check if we actually need to clear (state might already be empty)
          if (selectedKeys.size > 0) {
            setSelectedKeys(new Set());
          }
        } else {
          // Filter out empty strings and create new Set
          const newSelectedKeys = new Set(valueStr.split(',').filter(v => v && v.trim() !== ''));
          setSelectedKeys(newSelectedKeys);
        }
      } else {
        // Mark as processed even if we don't update state
        lastProcessedFilterInfoRef.current = filterInfoSignature;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.filterInfo]);

    const hasSelection = selectedKeys.size > 0;

    const updateFilterInfo = React.useCallback(
      (newSelectedKeys: Set<string>, newOperator?: string) => {
        const selectedIds = Array.from(newSelectedKeys);
        const selectedLabels = selectedIds.map(id => {
          const option = options.find(opt => opt.id === id);
          return option?.label ?? id;
        });

        const updatedFilterInfo: IFilterItem[] = [
          {
            ...currentFilterItem,
            type: newOperator ?? operator,
            value: selectedIds.join(','),
            valueLabel: selectedLabels.join(','),
          },
        ];
        
        // Mark this as processed BEFORE calling onFilterChange to prevent reprocessing
        lastProcessedFilterInfoRef.current = JSON.stringify({
          type: updatedFilterInfo[0].type,
          value: updatedFilterInfo[0].value,
          name: updatedFilterInfo[0].name,
        });
        
        const didNotify = notifyChange({
          ...filter,
          filterInfo: updatedFilterInfo,
        });
        if (didNotify) {
          isInternalUpdateRef.current = true;
          setTimeout(() => { isInternalUpdateRef.current = false; }, 0);
        }
      },
      [filter, currentFilterItem, operator, notifyChange, options]
    );

    const handleCheckboxChange = React.useCallback(
      (itemId: string, isSelected: boolean) => {
        const newSelectedKeys = new Set(selectedKeys);
        if (isSelected) {
          newSelectedKeys.add(itemId);
        } else {
          newSelectedKeys.delete(itemId);
        }
        setSelectedKeys(newSelectedKeys);
        updateFilterInfo(newSelectedKeys);
      },
      [selectedKeys, updateFilterInfo]
    );

    const handleOperatorChange = React.useCallback(
      (newOperator: string) => {
        setOperator(newOperator);
        updateFilterInfo(selectedKeys, newOperator);
      },
      [selectedKeys, updateFilterInfo]
    );

    const handleClearAll = React.useCallback(() => {
      const newSelectedKeys = new Set<string>();
      setSelectedKeys(newSelectedKeys);
      updateFilterInfo(newSelectedKeys);
    }, [updateFilterInfo]);

    return (
      <div ref={forwardedRef} className={styles.wrapper}>
        {/* Header: Title + Operator Picker */}
        <div className={styles.header}>
          <Text UNSAFE_className={styles.title}>{filter.label || filter.name}</Text>
          <div className={styles.operator}>
            <OperatorSelector
              operators={TYPE_OPERATORS}
              selectedOperator={operator}
              onOperatorChange={handleOperatorChange}
              aria-label="Folder type filter operator"
            />
          </div>
        </div>

        {/* Type Items with Checkboxes */}
        <div className={styles.itemsList}>
          {options.map((item) => {
            const isChecked = selectedKeys.has(item.id);
            return (
              <div key={item.id} className={styles.item}>
                <div className={styles.itemContent}>
                  <span className={styles.label}>{item.label}</span>
                </div>
                <AtollonCheckbox
                  isSelected={isChecked}
                  onChange={(checked) => handleCheckboxChange(item.id, checked)}
                  aria-label={item.label}
                  width='20px'
                />
              </div>
            );
          })}
        </div>

        {/* Clear All Link (conditional) */}
        {hasSelection && (
          <div className={styles.clear}>
            <Link onPress={handleClearAll} variant="primary" isQuiet UNSAFE_className={styles.clearLink}>
              Clear all
            </Link>
          </div>
        )}
      </div>
    );
  }
);

FolderTypeFilterControl.displayName = 'FolderTypeFilterControl';
